"use client";

import { useCategories } from "@/hooks/useCategories";
import { useTrendingTags } from "@/hooks/useAssets";
import { Slider } from "@/components/ui/slider";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import Star from "lucide-react/dist/esm/icons/star";
import X from "lucide-react/dist/esm/icons/x";
import ChevronDown from "lucide-react/dist/esm/icons/chevron-down";
import ChevronRight from "lucide-react/dist/esm/icons/chevron-right";
import Tag from "lucide-react/dist/esm/icons/tag";
import LayoutGrid from "lucide-react/dist/esm/icons/layout-grid";
import { resolveIcon } from "@/lib/category-icons";
import type { LucideIcon } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useState } from "react";
import { cn } from "@/lib/utils";
import type { BrowseLicense } from "@/lib/browse-url-state";
import { BROWSE_DEFAULTS } from "@/lib/browse-url-state";

interface BrowseFiltersProps {
  selectedCategory: string | null;
  onCategoryChange: (slug: string | null) => void;
  license: BrowseLicense;
  onLicenseChange: (value: BrowseLicense) => void;
  priceRange: [number, number];
  onPriceRangeChange: (range: [number, number]) => void;
  minRating: number;
  onMinRatingChange: (rating: number) => void;
  selectedTags: string[];
  onToggleTag: (tag: string) => void;
  onClearTags: () => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  /** When true, category rows close the mobile drawer after pick */
  closeOnCategorySelect?: boolean;
  onCategoryPicked?: () => void;
}

export function BrowseFilters({
  selectedCategory,
  onCategoryChange,
  license,
  onLicenseChange,
  priceRange,
  onPriceRangeChange,
  minRating,
  onMinRatingChange,
  selectedTags,
  onToggleTag,
  onClearTags,
  onClearFilters,
  hasActiveFilters,
  closeOnCategorySelect,
  onCategoryPicked,
}: BrowseFiltersProps) {
  const { data: categories, isLoading } = useCategories();
  const { data: trendingTags, isLoading: trendingLoading } = useTrendingTags(24);
  const [catOpen, setCatOpen] = useState(true);

  const getIcon = (iconName: string | null): LucideIcon => resolveIcon(iconName);

  const pickCategory = (slug: string | null) => {
    onCategoryChange(slug);
    if (closeOnCategorySelect) onCategoryPicked?.();
  };

  return (
    <aside className="w-full shrink-0 lg:w-[280px]">
      <div className="lg:sticky lg:top-24 space-y-6 pb-6">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-display font-semibold text-lg tracking-tight">Filters</h2>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              className="h-8 text-xs text-muted-foreground hover:text-primary"
            >
              <X className="h-3 w-3 mr-1" />
              Clear all
            </Button>
          )}
        </div>

        {/* License — pills */}
        <div className="space-y-3">
          <h3 className="font-mono text-[10px] uppercase tracking-widest text-foreground">License</h3>
          <div className="flex flex-wrap gap-1.5">
            {(
              [
                { id: "all" as const, label: "All" },
                { id: "free" as const, label: "Free" },
                { id: "paid" as const, label: "Paid" },
              ] as const
            ).map(({ id, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => onLicenseChange(id)}
                aria-pressed={license === id}
                className={cn(
                  "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                  license === id
                    ? "border-primary/50 bg-primary/15 text-foreground"
                    : "border-border/60 text-muted-foreground hover:border-primary/30 hover:bg-card",
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Categories — collapsible tree (flat list; room for nested later) */}
        <Collapsible open={catOpen} onOpenChange={setCatOpen}>
          <CollapsibleTrigger className="flex w-full items-center justify-between rounded-lg py-1 text-left font-mono text-[10px] uppercase tracking-widest text-foreground hover:text-muted-foreground">
            Categories
            {catOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-3 data-[state=closed]:animate-none">
            {isLoading ? (
              <div className="space-y-2">
                {[...Array(8)].map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full rounded-lg" style={{ animationDelay: `${i * 60}ms` }} />
                ))}
              </div>
            ) : (
              <div className="space-y-0.5 max-h-[min(50vh,320px)] overflow-y-auto pr-1 scrollbar-thin">
                <button
                  type="button"
                  onClick={() => pickCategory(null)}
                  aria-pressed={!selectedCategory}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200",
                    !selectedCategory
                      ? "bg-primary/10 text-primary border border-primary/20"
                      : "text-muted-foreground hover:bg-card hover:text-foreground border border-transparent",
                  )}
                >
                  <LayoutGrid className="h-4 w-4 shrink-0" />
                  All Categories
                </button>
                {categories?.map((category) => {
                  const Icon = getIcon(category.icon);
                  return (
                    <button
                      type="button"
                      key={category.id}
                      onClick={() => pickCategory(category.slug)}
                      aria-pressed={selectedCategory === category.slug}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200",
                        selectedCategory === category.slug
                          ? "bg-primary/10 text-primary border border-primary/20"
                          : "text-muted-foreground hover:bg-card hover:text-foreground border border-transparent",
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="truncate text-left">{category.name}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>

        {/* Tags — OR match with search filters */}
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-mono text-[10px] uppercase tracking-widest text-foreground flex items-center gap-1.5">
              <Tag className="h-3 w-3" aria-hidden />
              Tags
            </h3>
            {selectedTags.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-2xs text-muted-foreground"
                onClick={onClearTags}
              >
                Clear
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground leading-snug">
            Listings that match any selected tag. Text search also matches tag names.
          </p>
          {trendingLoading ? (
            <div className="flex flex-wrap gap-1.5">
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-7 w-16 rounded-full" style={{ animationDelay: `${i * 50}ms` }} />
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {(trendingTags ?? []).map(({ tag }: { tag: string }) => {
                const active = selectedTags.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => onToggleTag(tag)}
                    aria-pressed={active}
                    className={cn(
                      "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors max-w-[140px] truncate",
                      active
                        ? "border-primary/50 bg-primary/15 text-foreground"
                        : "border-border/60 text-muted-foreground hover:border-primary/30 hover:bg-card",
                    )}
                    title={tag}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Price */}
        <div className="space-y-4">
          <h3 id="browse-filter-price-heading" className="font-mono text-[10px] uppercase tracking-widest text-foreground">Price</h3>

          {license === "free" ? (
            <p className="text-xs text-muted-foreground">Price filters are hidden while &quot;Free&quot; license is selected.</p>
          ) : (
            <div className="space-y-4 pt-1" role="group" aria-labelledby="browse-filter-price-heading">
              <Slider
                value={priceRange}
                onValueChange={(value) => onPriceRangeChange(value as [number, number])}
                max={BROWSE_DEFAULTS.maxPrice}
                step={5}
                className="w-full"
              />
              <div className="flex items-center justify-between text-sm">
                <span className="px-2 py-1 rounded-md bg-card border border-border/50 font-mono text-xs">
                  ${priceRange[0]}
                </span>
                <span className="text-muted-foreground">to</span>
                <span className="px-2 py-1 rounded-md bg-card border border-border/50 font-mono text-xs">
                  ${priceRange[1]}+
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Rating */}
        <div className="space-y-4">
          <h3 className="font-mono text-[10px] uppercase tracking-widest text-foreground">Minimum Rating</h3>
          <div className="space-y-1">
            {[0, 3, 4, 4.5].map((rating) => (
              <button
                key={rating}
                type="button"
                onClick={() => onMinRatingChange(rating)}
                aria-pressed={minRating === rating}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200",
                  minRating === rating
                    ? "bg-warning/15 text-foreground border border-warning/40"
                    : "text-muted-foreground hover:bg-card hover:text-foreground border border-transparent",
                )}
              >
                {rating === 0 ? (
                  <span className="font-medium">Any rating</span>
                ) : (
                  <>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={cn(
                            "h-3.5 w-3.5",
                            i < Math.floor(rating)
                              ? "fill-warning text-warning"
                              : i < rating
                                ? "fill-warning/50 text-warning"
                                : "text-muted-foreground/30",
                          )}
                        />
                      ))}
                    </div>
                    <span className="font-medium">{rating}+ stars</span>
                  </>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </aside>
  );
}
